import type { ImportRunRow } from "@gioia365/contracts";
import { formatNumericDateTime } from "../i18n/format";
import type { Translate } from "../i18n/locale";

// Date e durate della pagina «Importazione da Close» (piano M2 Task 21): giorno e ora nel fuso dell'azienda della scheda,
// in numeri come in tutto il prodotto (decisione del 25 settembre 2026), e le durate in parole brevi.

/** «30/09/2026 18:05» nel fuso dell'azienda, e le due parti per le frasi («del … alle …»). */
export function whenParts(timeZone: string, iso: string): { full: string; date: string; time: string } {
  const full = formatNumericDateTime(timeZone, iso);
  const [date = full, time = ""] = full.split(" ");
  return { full, date, time };
}

/** «Prova senza salvare del 30/09/2026 alle 18:05» oppure «Importazione del …». */
export function runTitle(t: Translate, timeZone: string, run: ImportRunRow): string {
  const { date, time } = whenParts(timeZone, run.startedAt);
  return t(run.mode === "dry_run" ? "closeImport.when.dryRun" : "closeImport.when.apply", { date, time });
}

/** Una durata in parole brevi: «5 s», «41 min», «1 h 52 min». */
export function formatDuration(t: Translate, milliseconds: number): string {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  if (seconds < 60) return t("closeImport.duration.seconds", { count: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t("closeImport.duration.minutes", { count: minutes });
  return t("closeImport.duration.hours", { hours: Math.floor(minutes / 60), minutes: minutes % 60 });
}

/**
 * Quanto è durata un'esecuzione finita (fino alla fine) o fermata (fino all'ultimo battito), o da quanto è in corso
 * all'istante `now`. Una fermata si chiude anche molto dopo il suo ultimo segno di vita (con `--abandon`, o sostituita
 * alla ripresa): la sua fine registrata non dice quanto ha lavorato. Le pagine passano come `now` l'ora dell'ultima
 * lettura dal server (`dataUpdatedAt` della query), che per un'esecuzione in corso cambia a ogni rilettura: il disegno
 * resta puro.
 */
export function runDuration(run: ImportRunRow, now: number): number {
  if (run.status === "running") return now - Date.parse(run.startedAt);
  const end = run.status === "completed" ? (run.finishedAt ?? run.heartbeatAt) : run.heartbeatAt;
  return Date.parse(end) - Date.parse(run.startedAt);
}
